import * as React from "react";
import { cn } from "@/lib/cn";

export type ButtonVariant =
  | "default"
  | "destructive"
  | "success"
  | "outline"
  | "secondary"
  | "ghost"
  | "link";

export type ButtonSize = "default" | "sm" | "lg" | "icon";

const BASE =
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-xl text-sm font-semibold transition-[transform,background-color,color,border-color] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 disabled:pointer-events-none disabled:opacity-60 active:scale-[0.98] [&_svg]:pointer-events-none [&_svg]:shrink-0";

const VARIANT_CLASSES: Record<ButtonVariant, string> = {
  default: "bg-primary text-white shadow-sm hover:bg-primary/90",
  destructive: "bg-danger text-white shadow-sm hover:bg-danger/90",
  success: "bg-success text-white shadow-sm hover:bg-success/90",
  outline:
    "border border-default-200 bg-background text-foreground hover:bg-content2",
  secondary: "bg-content3 text-default-700 hover:bg-content3/70",
  ghost: "text-foreground hover:bg-content2",
  link: "text-primary underline-offset-4 hover:underline",
};

const SIZE_CLASSES: Record<ButtonSize, string> = {
  default: "h-11 px-5 py-2",
  sm: "h-9 rounded-lg px-4 text-xs",
  lg: "h-12 rounded-xl px-6 text-sm",
  icon: "size-11",
};

/**
 * Resolve the button class string for a variant/size — handy for styling a
 * non-button element (e.g. a router `<Link>`) like a button:
 * `<Link className={buttonVariants({ variant: "outline" })} />`.
 */
export function buttonVariants({
  variant = "default",
  size = "default",
  className,
}: {
  variant?: ButtonVariant;
  size?: ButtonSize;
  className?: string;
} = {}) {
  return cn(BASE, VARIANT_CLASSES[variant], SIZE_CLASSES[size], className);
}

function Spinner() {
  return (
    <svg
      className="size-4 animate-spin"
      viewBox="0 0 24 24"
      fill="none"
      aria-hidden="true"
    >
      <circle
        className="opacity-25"
        cx="12"
        cy="12"
        r="10"
        stroke="currentColor"
        strokeWidth="4"
      />
      <path
        className="opacity-75"
        fill="currentColor"
        d="M4 12a8 8 0 0 1 8-8v4a4 4 0 0 0-4 4H4z"
      />
    </svg>
  );
}

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  /** Render the single child element instead of a `<button>`, keeping the styles. */
  asChild?: boolean;
  /** Show a spinner and block interaction while an action is in flight. */
  loading?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  {
    className,
    variant = "default",
    size = "default",
    asChild = false,
    loading = false,
    type,
    disabled,
    children,
    ...props
  },
  ref,
) {
  const classes = buttonVariants({ variant, size, className });

  // `asChild` clones the child (e.g. <Link>/<a>) and merges the button styles —
  // a lightweight stand-in for @radix-ui/react-slot.
  if (asChild && React.isValidElement(children)) {
    const child = children as React.ReactElement<{ className?: string }>;
    return React.cloneElement(child, {
      className: cn(classes, child.props.className),
      ...props,
    } as Record<string, unknown>);
  }

  return (
    <button
      ref={ref}
      type={type ?? "button"}
      className={classes}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      {...props}
    >
      {loading ? <Spinner /> : null}
      {children}
    </button>
  );
});

export default Button;
